/**
 * Audio mixing & auto-duck (PRD §6: two volume sliders + auto-duck the movie
 * when someone speaks). The decision logic is pure and tested here; the Web
 * Audio wiring (`AudioMixer`) is exercised by the e2e proxy.
 */
export interface DuckConfig {
  /** Movie gain multiplier when nobody is speaking. */
  normal: number;
  /** Movie gain multiplier while someone speaks. */
  ducked: number;
  /** Keep ducking this long after the last speech frame (ms). */
  holdMs: number;
  attackMs: number;
  releaseMs: number;
}

export const DEFAULT_DUCK: DuckConfig = {
  normal: 1.0,
  ducked: 0.25,
  holdMs: 400,
  attackMs: 120,
  releaseMs: 600,
};

/** Target movie-gain multiplier given whether anyone is currently speaking. */
export function duckTarget(anyoneSpeaking: boolean, cfg: DuckConfig = DEFAULT_DUCK): number {
  return anyoneSpeaking ? cfg.ducked : cfg.normal;
}

/**
 * Decide, at time `nowMs`, whether the movie should be ducked given the last
 * time speech was detected. Implements the hold window so the movie doesn't
 * pump on every inter-word gap. Pure for testability.
 */
export function shouldDuck(lastSpeechMs: number | null, nowMs: number, cfg: DuckConfig = DEFAULT_DUCK): boolean {
  if (lastSpeechMs === null) return false;
  return nowMs - lastSpeechMs <= cfg.holdMs;
}

/**
 * Simple speaking detector from a normalized audio level in [0, 1] with
 * hysteresis: rises above `onThreshold` → speaking; must fall below
 * `offThreshold` to stop. Returns the new speaking state.
 */
export function detectSpeaking(
  level: number,
  wasSpeaking: boolean,
  onThreshold = 0.05,
  offThreshold = 0.02,
): boolean {
  if (wasSpeaking) return level >= offThreshold;
  return level >= onThreshold;
}
